import { useEffect, useState } from 'react';
import type { CompanyProfile } from '@swg/contracts';
import { CONFIDENCE_LABELS, type Confidence } from '@swg/domain';
import { ApiError, confirmProfile, fetchProfile, patchProfile } from '../api.js';
import { theme } from '../theme.js';

const CONFIDENCE_COLOR: Record<Confidence, string> = {
  low: '#C99A2E',
  medium: theme.color.primary,
  high: '#1E7A44',
};

type FieldType = 'text' | 'list';

interface FieldDef {
  key: 'name' | 'industry' | 'services' | 'geographicFocus' | 'targetAudience' | 'propositions' | 'keywords';
  label: string;
  type: FieldType;
  span: 1 | 2;
}

// Volgorde en kolombreedte conform het goedgekeurde prototype.
const FIELD_LAYOUT: FieldDef[] = [
  { key: 'name', label: 'Bedrijfsnaam', type: 'text', span: 1 },
  { key: 'industry', label: 'Branche', type: 'text', span: 1 },
  { key: 'services', label: 'Belangrijkste producten / diensten', type: 'list', span: 2 },
  { key: 'geographicFocus', label: 'Geografische focus', type: 'text', span: 1 },
  { key: 'targetAudience', label: 'Doelgroep', type: 'text', span: 1 },
  { key: 'propositions', label: 'Belangrijkste proposities', type: 'list', span: 2 },
  { key: 'keywords', label: 'Relevante zoekwoorden', type: 'list', span: 2 },
];

function toLines(value: string): string[] {
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function textValue(p: CompanyProfile, key: FieldDef['key']): string {
  const v = p[key as 'name'];
  return (v as string | null) ?? '';
}

function listValue(p: CompanyProfile, key: FieldDef['key']): string[] {
  return (p[key as 'services'] as string[]) ?? [];
}

export function ProfileReview({
  runId,
  runActive,
  canEdit,
  onConfirmed,
  onBack,
}: {
  runId: string;
  runActive: boolean;
  canEdit: boolean;
  onConfirmed: () => void;
  onBack?: () => void;
}) {
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [state, setState] = useState<'loading' | 'none' | 'ready'>('loading');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // KVK is een apart, altijd-bewerkbaar veld (hoort bij het bedrijf, niet de versie).
  const [kvk, setKvk] = useState('');
  const [kvkBusy, setKvkBusy] = useState(false);
  const [kvkSaved, setKvkSaved] = useState(false);
  const [kvkError, setKvkError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchProfile(runId)
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        setKvk(p.kvkNumber ?? '');
        setState('ready');
      })
      .catch(() => !cancelled && setState('none'));
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (state === 'loading') return null;
  if (state === 'none') {
    return runActive ? (
      <p style={{ color: theme.color.muted, fontSize: 13.5 }}>Het bedrijfsprofiel wordt opgesteld…</p>
    ) : null;
  }
  if (!profile) return null;

  const confirmed = profile.status === 'CONFIRMED';
  const editable = canEdit && !confirmed;

  function startEdit(f: FieldDef) {
    setError(null);
    setEditingField(f.key);
    setDraft(f.type === 'list' ? listValue(profile!, f.key).join('\n') : textValue(profile!, f.key));
  }

  async function saveField(f: FieldDef) {
    setError(null);
    setBusy(true);
    try {
      const patch =
        f.type === 'list' ? { [f.key]: toLines(draft) } : { [f.key]: draft.trim() };
      const updated = await patchProfile(runId, patch);
      setProfile(updated);
      setEditingField(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm() {
    setError(null);
    setBusy(true);
    try {
      const updated = await confirmProfile(runId);
      setProfile(updated);
      onConfirmed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Bevestigen mislukt.');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveKvk() {
    setKvkError(null);
    setKvkSaved(false);
    const value = kvk.trim();
    if (value !== '' && !/^\d{8}$/.test(value)) {
      setKvkError('KVK-nummer moet 8 cijfers zijn.');
      return;
    }
    setKvkBusy(true);
    try {
      const updated = await patchProfile(runId, { kvkNumber: value });
      setProfile(updated);
      setKvk(updated.kvkNumber ?? '');
      setKvkSaved(true);
    } catch (err) {
      setKvkError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    } finally {
      setKvkBusy(false);
    }
  }

  // Unieke bronnen voor de "Gebruikte bronnen"-voettekst.
  const usedSources = Array.from(
    new Set(
      Object.values(profile.fieldMeta)
        .map((m) => m?.source)
        .filter((s): s is string => Boolean(s)),
    ),
  );

  const cardStyle = {
    background: theme.color.surface,
    border: `1px solid ${theme.color.border}`,
    borderRadius: 10,
    padding: '16px 18px',
  } as const;

  const inputStyle = {
    width: '100%',
    padding: '9px 11px',
    fontSize: 14,
    border: `1px solid ${theme.color.borderStrong}`,
    borderRadius: 8,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  };

  const badge = (bg: string, color: string, text: string) => (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        background: bg,
        color,
        borderRadius: 10,
        padding: '2px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>
          Bedrijfsprofiel{profile.name ? ` — ${profile.name}` : ''}
        </h2>
        <div style={{ fontSize: 13, color: theme.color.muted }}>
          {confirmed ? (
            <span style={{ color: theme.color.ok, fontWeight: 600 }}>Bevestigd</span>
          ) : (
            'Automatisch opgesteld — controleer voordat de analyse verdergaat.'
          )}
        </div>
      </div>

      {!confirmed && (
        <div
          style={{
            background: theme.color.warnBg,
            border: `1px solid ${theme.color.warnBorder}`,
            borderRadius: 10,
            padding: '12px 16px',
            fontSize: 13.5,
            color: '#6E5410',
            marginBottom: 18,
            lineHeight: 1.5,
          }}
        >
          <strong>Controleer dit profiel voordat de analyse verdergaat.</strong> Onzekere of
          afgeleide gegevens zijn gemarkeerd als <em>aanname</em>. Correcties worden bewaard en niet
          zonder waarschuwing overschreven.
        </div>
      )}

      {/* KVK-nummer (apart, altijd bewerkbaar) */}
      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 7 }}>
          KVK-nummer <span style={{ textTransform: 'none', fontWeight: 400, color: theme.color.subtle }}>(8 cijfers; voor officiële branche via KVK)</span>
        </div>
        {canEdit ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              style={{ ...inputStyle, maxWidth: 180 }}
              inputMode="numeric"
              placeholder="12345678"
              value={kvk}
              onChange={(e) => setKvk(e.target.value.replace(/\D/g, '').slice(0, 8))}
            />
            <button
              onClick={() => void onSaveKvk()}
              disabled={kvkBusy}
              style={{ background: 'transparent', color: theme.color.text, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: kvkBusy ? 'default' : 'pointer', fontFamily: 'inherit' }}
            >
              {kvkBusy ? 'Bezig…' : 'Bewaren'}
            </button>
            {kvkSaved && <span style={{ fontSize: 12.5, color: theme.color.ok }}>Opgeslagen ✓</span>}
          </div>
        ) : (
          <div style={{ fontSize: 14 }}>{kvk || <span style={{ color: theme.color.subtle }}>—</span>}</div>
        )}
        {kvkError && <div style={{ marginTop: 6, fontSize: 12.5, color: '#A82A2A' }}>{kvkError}</div>}
      </div>

      {/* Velden in 2-koloms grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {FIELD_LAYOUT.map((f) => {
          const meta = profile.fieldMeta[f.key];
          const isAssumption = meta?.origin === 'AI_SUGGESTED';
          const isCorrected = meta?.origin === 'MANUAL';
          const editing = editingField === f.key;
          const list = f.type === 'list' ? listValue(profile, f.key) : [];
          const text = f.type === 'text' ? textValue(profile, f.key) : '';

          return (
            <div key={f.key} style={{ ...cardStyle, gridColumn: `span ${f.span}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 7 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {f.label}
                  </span>
                  {isAssumption && badge(theme.color.warnBg, '#8A6A12', 'AANNAME')}
                  {isCorrected && badge('#E4F3EB', theme.color.ok, 'GECORRIGEERD')}
                </div>
                {editable && !editing && (
                  <button
                    onClick={() => startEdit(f)}
                    style={{ background: 'none', border: 'none', color: theme.color.primary, fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', padding: 0, whiteSpace: 'nowrap' }}
                  >
                    Bewerken
                  </button>
                )}
              </div>

              {editing ? (
                <div>
                  {f.type === 'list' ? (
                    <textarea
                      style={{ ...inputStyle, minHeight: 76, resize: 'vertical' }}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Eén per regel"
                    />
                  ) : (
                    <input style={inputStyle} value={draft} onChange={(e) => setDraft(e.target.value)} />
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      onClick={() => void saveField(f)}
                      disabled={busy}
                      style={{ background: theme.color.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}
                    >
                      {busy ? 'Bezig…' : 'Opslaan'}
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      disabled={busy}
                      style={{ background: 'transparent', color: theme.color.text, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              ) : f.type === 'list' ? (
                list.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {list.map((v, i) => (
                      <span key={i} style={{ fontSize: 12.5, background: theme.color.accentBg, color: theme.color.primary, borderRadius: 6, padding: '3px 9px' }}>
                        {v}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: theme.color.subtle, fontSize: 14 }}>—</span>
                )
              ) : (
                <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                  {text || <span style={{ color: theme.color.subtle }}>—</span>}
                </div>
              )}

              {meta && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11.5, color: theme.color.muted }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: CONFIDENCE_COLOR[meta.confidence], display: 'inline-block' }} />
                  <span>{CONFIDENCE_LABELS[meta.confidence]}</span>
                  <span>·</span>
                  <span>bron: {meta.source}</span>
                  {isAssumption && <span style={{ color: '#7c3aed', fontWeight: 600 }}>· AI-suggestie</span>}
                  {isCorrected && <span style={{ color: theme.color.primary, fontWeight: 600 }}>· handmatig</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Gebruikte bronnen */}
      {usedSources.length > 0 && (
        <div style={{ ...cardStyle, marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Gebruikte bronnen
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {usedSources.map((s) => (
              <span key={s} style={{ fontSize: 12.5, background: theme.color.bg, color: theme.color.text, border: `1px solid ${theme.color.border}`, borderRadius: 8, padding: '4px 10px' }}>
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: '#FBE6E6', border: '1px solid #EFC2C2', color: '#A82A2A', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginTop: 14 }}>
          {error}
        </div>
      )}

      {/* Onderste acties */}
      {editable && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20 }}>
          <button
            onClick={() => onBack?.()}
            disabled={!onBack}
            style={{ background: 'transparent', color: theme.color.text, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: onBack ? 'pointer' : 'default', opacity: onBack ? 1 : 0.5, fontFamily: 'inherit' }}
          >
            ← Terug
          </button>
          <button
            onClick={() => void onConfirm()}
            disabled={busy}
            style={{ background: theme.color.cta, color: theme.color.ctaText, border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'inherit' }}
          >
            {busy ? 'Bezig…' : 'Profiel bevestigen → concurrenten'}
          </button>
        </div>
      )}
    </div>
  );
}
